// src/views/BackOffice/ManageRepresentatives.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Alert,
  Spinner,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import axios from '../../api/Axios';

const ManageRepresentatives = () => {
  const [activeTab, setActiveTab] = useState('primary');
  const [primaryReps, setPrimaryReps] = useState([]);
  const [secondaryReps, setSecondaryReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRepresentatives();
  }, []);

  const fetchRepresentatives = async () => {
    try {
      setLoading(true);
      const [primaryResponse, secondaryResponse] = await Promise.all([
        axios.get('accounts/primary-representatives/'),
        axios.get('accounts/secondary-representatives/')
      ]);
      
      setPrimaryReps(primaryResponse.data);
      setSecondaryReps(secondaryResponse.data);
      setError('');
    } catch (error) {
      console.error('Error fetching representatives:', error);
      setError('Failed to load representatives. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderTable = (representatives, type) => (
    <div className="table-responsive">
      <Table striped hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>BMO Organization</th>
            <th>Status</th>
            <th>Created By</th>
            <th>Created Date</th>
          </tr>
        </thead>
        <tbody>
          {representatives.map((rep) => (
            <tr key={rep.id}>
              <td>
                <strong>
                  {rep.title && `${rep.title} `}
                  {rep.first_name} {rep.last_name}
                </strong>
              </td>
              <td>
                <small className="text-muted">{rep.email_address}</small>
              </td>
              <td>
                <small className="text-muted">{rep.phone_number}</small>
              </td>
              <td>
                <Badge color="info" className="me-1">
                  {rep.bmo_name}
                </Badge>
              </td>
              <td>
                <Badge color={rep.is_activated ? 'success' : 'warning'}>
                  {rep.is_activated ? 'Activated' : 'Pending'}
                </Badge>
              </td>
              <td>
                <small className="text-muted">{rep.created_by_email}</small>
              </td>
              <td>
                <small className="text-muted">{formatDate(rep.created_at)}</small>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading representatives...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-4">Manage Representatives</h2>
      
      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Card>
        <CardBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === 'primary' ? 'active' : ''}
                onClick={() => setActiveTab('primary')}
                style={{ cursor: 'pointer' }}
              >
                Primary Representatives
                <Badge color="primary" className="ms-2">
                  {primaryReps.length}
                </Badge>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'secondary' ? 'active' : ''}
                onClick={() => setActiveTab('secondary')}
                style={{ cursor: 'pointer' }}
              >
                Secondary Representatives
                <Badge color="secondary" className="ms-2">
                  {secondaryReps.length}
                </Badge>
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="primary">
              <div className="pt-3">
                <CardTitle tag="h5">Primary Representatives</CardTitle>
                {primaryReps.length === 0 ? (
                  <Alert color="info">
                    No Primary Representatives found. Create your first Primary Representative to get started.
                  </Alert>
                ) : (
                  renderTable(primaryReps, 'primary')
                )}
              </div>
            </TabPane>
            
            <TabPane tabId="secondary">
              <div className="pt-3">
                <CardTitle tag="h5">Secondary Representatives</CardTitle>
                {secondaryReps.length === 0 ? (
                  <Alert color="info">
                    No Secondary Representatives found. Create your first Secondary Representative to get started.
                  </Alert>
                ) : (
                  renderTable(secondaryReps, 'secondary')
                )}
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default ManageRepresentatives;
